import { Injectable, OnDestroy } from "@angular/core";
import { CoreLogService } from "../../services/log.service";
import { Observable } from "rxjs/Observable";
import { ContentType } from "../../shared/contentTypes";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ServiceCallDataModel, WebshopSettingsDataModel2, InHouseOrder, WebshopSettingsDMKey } from "../../shared/models";
import { BehaviorSubject, Subscription } from "rxjs";
import { InHouseOrderService } from "../../services/inHouseOrder.service";

@Injectable()
export class InHauseServiceSelectorService implements OnDestroy {
  private _inHouseTableChangedSub: Subscription;

  private _servciecallObsServer: IObjectObserver<ServiceCallDataModel>;
  private _webshopSettingsObsServer: IObjectObserver<WebshopSettingsDataModel2>;

  constructor(private _coreLogService: CoreLogService,
    private _inHouseOrderService: InHouseOrderService,
    objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    me._servciecallObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ServiceCallDataModel>(ContentType.Inhouse_ServiceCalls, objObsInjectHelper);
    me._webshopSettingsObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, objObsInjectHelper);

    me._inHouseTableChangedSub = me._inHouseOrderService.ObserveOnTableChanged.subscribe(async (_) => {
      const table = await me._inHouseOrderService.GetTable(null); // null damit kein unnötiger Abruf stattfindet
      me.setReadyForPickUpCount(me._inHouseOrderService.GetReadyForPickUpCount(table));
    });
  }

  ngOnDestroy(): void {
    // Führt Cleanup durch
    const me = this;

    if (me._inHouseTableChangedSub)
      me._inHouseTableChangedSub.unsubscribe();
  }

  public get ObserveServiceCallsChanges(): Observable<(string | number)[]> {
    return this._servciecallObsServer.ObserveChanges;
  }

  public get ObserveWebshopSettingsInHouseOrderChanges(): Observable<(string | number)[]> {
    return this._webshopSettingsObsServer.ObserveChanges;
  }

  /** use setReadyForPickUpCount */
  private _readyForPickUpCount: number = 0;
  /** aktualisiert nicht abgeholter artikel */
  private setReadyForPickUpCount(value: number): void {
    const me = this;
    if (me._readyForPickUpCount != value) {
      me._readyForPickUpCount = value;
      me._readyForPickUpCountSubject.next(value);
    }
  }
  private _readyForPickUpCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get readyForPickUpCount$(): Observable<number> {
    return this._readyForPickUpCountSubject.asObservable();
  }

  public GetServiceCalls(): Promise<ServiceCallDataModel[]> {
    const me = this;
    return new Promise<ServiceCallDataModel[]>((resolve, reject) => {
      me._servciecallObsServer.GetObjectsByFilter(model => model.enabled && !model.deleted)
        .then((service_calls) => {
          service_calls.sort((l, r) => {
            if (l.sort_number < r.sort_number) {
              return -1;
            } else if (l.sort_number = r.sort_number) {
              return 1;
            } else {
              return 0;
            }
          });
          resolve(service_calls);
        }).catch((error) => {
          me._coreLogService.error("InHauseServiceSelectorService.GetServiceCalls", "GetObjectsByFilter", error);
          reject();
        });
    });
  }

  /** Bezahlung erfolgt nach Bestellung, sonst bei Bestellung */
  public OrderInHouseWithoutPayment(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me._webshopSettingsObsServer.GetObject(WebshopSettingsDMKey)
        .then((webshopSettings) => {
          // Gruppenbestellungen, zahlen immer nach Bestellung
          resolve(webshopSettings.order_inhouse_without_payment || me._inHouseOrderService.UiData.isGroup);
        }).catch((error) => {
          me._coreLogService.error("InHauseServiceSelectorService.OrderInHouseWithoutPayment", "GetObject", error);
          reject();
        });
    });
  }

  /** Bezahlung möglich, jedoch ist die Bestellung nicht möglich */
  public PaymentInhouseWithoutOrder(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me._webshopSettingsObsServer.GetObject(WebshopSettingsDMKey)
        .then((webshopSettings) => {
          resolve(webshopSettings.payment_inhouse_without_order);
        }).catch((error) => {
          me._coreLogService.error("InHauseServiceSelectorService.PaymentInhouseWithoutOrder", "GetObject", error);
          reject();
        });
    });
  }

  public GetWebshopSettingsInHouseOrder(): Promise<InHouseOrder> {
    const me = this;
    return new Promise<InHouseOrder>((resolve, reject) => {
      me._webshopSettingsObsServer.GetObject(WebshopSettingsDMKey)
        .then((webshopSettings) => {
          resolve(webshopSettings.in_house_order);
        }).catch((error) => {
          me._coreLogService.error("InHauseServiceSelectorService.GetWebshopSettingsInHouseOrder", "GetObject", error);
          reject();
        });
    });
  }
}
